import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import moment from 'moment-timezone'
import { SIGNUP_TYPES, getSignupType, usesTimeSlots, requiresDetail, formatSlotLabel } from '../utils/signupSheets'

const ET = 'America/New_York'

const EventManagement = () => {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [showHistorical, setShowHistorical] = useState(false)
  const [flyerFile, setFlyerFile] = useState(null)
  const [flyerPreview, setFlyerPreview] = useState(null)
  const [removeFlyerFlag, setRemoveFlyerFlag] = useState(false)
  const [showRsvpModal, setShowRsvpModal] = useState(false)
  const [selectedEventForRsvp, setSelectedEventForRsvp] = useState(null)
  const [rsvpData, setRsvpData] = useState(null)
  const [loadingRsvps, setLoadingRsvps] = useState(false)
  const [rsvpCounts, setRsvpCounts] = useState({})
  const [editingRsvp, setEditingRsvp] = useState(null)
  const [editRsvpValues, setEditRsvpValues] = useState({ numAdults: 0, numChildren: 0 })
  const [signupRoles, setSignupRoles] = useState([])
  const [showSignupModal, setShowSignupModal] = useState(false)
  const [selectedEventForSignup, setSelectedEventForSignup] = useState(null)
  const [signupData, setSignupData] = useState(null)
  const [loadingSignups, setLoadingSignups] = useState(false)
  const [editingSignup, setEditingSignup] = useState(null)
  const [editSignupValues, setEditSignupValues] = useState({ attendeeName: '', attendeeEmail: '', detail: '' })
  const [signupCounts, setSignupCounts] = useState({})

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm()

  // Watch collectPayment to show/hide price fields
  const collectPayment = watch('collectPayment', false)
  // Watch bookFacility to show/hide facility selection
  const bookFacility = watch('bookFacility', false)
  // Watch hasSignupSheet to show/hide role builder
  const hasSignupSheet = watch('hasSignupSheet', false)
  // Watch startTime to auto-fill endTime
  const startTime = watch('startTime')

  // Generate time options in 30-minute increments
  const generateTimeOptions = () => {
    const times = []
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = hour.toString().padStart(2, '0')
        const minStr = minute.toString().padStart(2, '0')
        const timeValue = `${hourStr}:${minStr}`
        const displayTime = moment(timeValue, 'HH:mm').format('h:mm A')
        times.push({ value: timeValue, label: displayTime })
      }
    }
    return times
  }

  const timeOptions = generateTimeOptions()

  // Auto-fill end time to 4 hours after start time
  useEffect(() => {
    if (startTime && !editingEvent) {
      const endMoment = moment(startTime, 'HH:mm').add(4, 'hours')
      setValue('endTime', endMoment.format('HH:mm'))
    }
  }, [startTime, setValue, editingEvent])

  // Fetch events
  const fetchEvents = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get('/api/events')
      setEvents(response.data)

      // Fetch RSVP counts for events that require RSVP or collect payment
      const eventsWithRsvp = response.data.filter(event => event.requireRsvp || event.collectPayment)
      if (eventsWithRsvp.length > 0) {
        fetchRsvpCounts(eventsWithRsvp)
      }

      // Fetch signup counts for events with sign-up sheets
      const eventsWithSignup = response.data.filter(event => event.hasSignupSheet)
      if (eventsWithSignup.length > 0) {
        fetchSignupCounts(eventsWithSignup)
      }
    } catch (err) {
      setError('Failed to load events. Please try again.')
      console.error('Error fetching events:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch RSVP counts for multiple events
  const fetchRsvpCounts = async (events) => {
    try {
      const counts = {}
      await Promise.all(
        events.map(async (event) => {
          try {
            const response = await axios.get(`/api/events/${event._id}/rsvp-count`)
            counts[event._id] = response.data
          } catch (err) {
            console.error(`Error fetching RSVP count for event ${event._id}:`, err)
            counts[event._id] = { rsvpCount: 0, totalAttendees: 0 }
          }
        })
      )
      setRsvpCounts(counts)
    } catch (err) {
      console.error('Error fetching RSVP counts:', err)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  // Fetch signup counts for events with sign-up sheets
  const fetchSignupCounts = async (events) => {
    try {
      const counts = {}
      await Promise.all(
        events.map(async (event) => {
          try {
            const response = await axios.get(`/api/events/${event._id}/signup-count`)
            counts[event._id] = response.data
          } catch (err) {
            console.error(`Error fetching signup count for event ${event._id}:`, err)
            counts[event._id] = { signupCount: 0 }
          }
        })
      )
      setSignupCounts(prev => ({ ...prev, ...counts }))
    } catch (err) {
      console.error('Error fetching signup counts:', err)
    }
  }

  // Signup role/slot builder helpers
  const newSlot = () => ({
    slotId: `slot_${Date.now()}`,
    startTime: '',
    endTime: '',
    label: '',
    capacity: 1,
  })

  const addRole = (type) => {
    setSignupRoles(prev => [
      ...prev,
      {
        roleId: `role_${Date.now()}`,
        roleName: '',
        type,
        description: '',
        slots: [newSlot()],
      },
    ])
  }

  const removeRole = (roleId) => {
    setSignupRoles(prev => prev.filter(r => r.roleId !== roleId))
  }

  const updateRole = (roleId, field, value) => {
    setSignupRoles(prev => prev.map(r => (r.roleId === roleId ? { ...r, [field]: value } : r)))
  }

  const addSlot = (roleId) => {
    setSignupRoles(prev =>
      prev.map(r => (r.roleId === roleId ? { ...r, slots: [...r.slots, newSlot()] } : r))
    )
  }

  const removeSlot = (roleId, slotId) => {
    setSignupRoles(prev =>
      prev.map(r =>
        r.roleId === roleId ? { ...r, slots: r.slots.filter(s => s.slotId !== slotId) } : r
      )
    )
  }

  const updateSlot = (roleId, slotId, field, value) => {
    setSignupRoles(prev =>
      prev.map(r =>
        r.roleId === roleId
          ? {
              ...r,
              slots: r.slots.map(s =>
                s.slotId === slotId ? { ...s, [field]: field === 'capacity' ? parseInt(value) || 1 : value } : s
              ),
            }
          : r
      )
    )
  }

  // View signups for an event
  const handleViewSignups = async (event) => {
    try {
      setLoadingSignups(true)
      setSelectedEventForSignup(event)
      setShowSignupModal(true)

      const response = await axios.get(`/api/events/${event._id}/signups`)
      setSignupData(response.data)
    } catch (err) {
      console.error('Error fetching signups:', err)
      alert('Failed to load signups: ' + (err.response?.data?.message || err.message))
      setShowSignupModal(false)
    } finally {
      setLoadingSignups(false)
    }
  }

  const closeSignupModal = () => {
    setShowSignupModal(false)
    if (selectedEventForSignup && signupData) {
      setSignupCounts(prev => ({
        ...prev,
        [selectedEventForSignup._id]: { signupCount: signupData.summary.totalSignups },
      }))
    }
    setSelectedEventForSignup(null)
    setSignupData(null)
    setEditingSignup(null)
  }

  const startEditSignup = (signup) => {
    setEditingSignup(signup._id)
    setEditSignupValues({
      attendeeName: signup.attendeeName,
      attendeeEmail: signup.attendeeEmail,
      detail: signup.detail || '',
    })
  }

  const saveEditSignup = async (signupId) => {
    try {
      await axios.put(`/api/events/${selectedEventForSignup._id}/signups/${signupId}`, editSignupValues)
      const response = await axios.get(`/api/events/${selectedEventForSignup._id}/signups`)
      setSignupData(response.data)
      setEditingSignup(null)
    } catch (err) {
      alert('Failed to update signup: ' + (err.response?.data?.message || err.message))
    }
  }

  const deleteSignup = async (signupId) => {
    if (!window.confirm('Remove this signup?')) return
    try {
      await axios.delete(`/api/events/${selectedEventForSignup._id}/signups/${signupId}`)
      const response = await axios.get(`/api/events/${selectedEventForSignup._id}/signups`)
      setSignupData(response.data)
    } catch (err) {
      alert('Failed to remove signup: ' + (err.response?.data?.message || err.message))
    }
  }

  // Open modal for creating/editing
  const openModal = (event = null) => {
    setEditingEvent(event)
    setFlyerFile(null)
    setFlyerPreview(event?.flyerImageUrl || null)
    setRemoveFlyerFlag(false)
    if (event) {
      // Format date for input field
      const formattedEvent = {
        ...event,
        eventDate: event.eventDate ? new Date(event.eventDate).toISOString().split('T')[0] : '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
      }
      reset(formattedEvent)
      setSignupRoles(event.signupRoles || [])
    } else {
      reset({
        title: '',
        description: '',
        eventDate: '',
        startTime: '',
        endTime: '',
        location: '',
        category: 'general',
        isPublished: true,
        maxAttendees: '',
        requireRsvp: false,
        collectPayment: false,
        pricePerAdult: '',
        pricePerChild: '',
        bookFacility: false,
        facility: null,
        hasSignupSheet: false,
        notifyMembers: false,
      })
      setSignupRoles([])
    }
    setShowModal(true)
  }

  // Close modal
  const closeModal = () => {
    setShowModal(false)
    setEditingEvent(null)
    setFlyerFile(null)
    setFlyerPreview(null)
    setRemoveFlyerFlag(false)
    setSignupRoles([])
    reset()
  }

  // Handle flyer file selection
  const handleFlyerChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFlyerFile(file)
      setRemoveFlyerFlag(false)
      const reader = new FileReader()
      reader.onloadend = () => setFlyerPreview(reader.result)
      reader.readAsDataURL(file)
    }
  }

  // Handle flyer removal
  const handleRemoveFlyer = () => {
    setFlyerFile(null)
    setFlyerPreview(null)
    setRemoveFlyerFlag(true)
  }

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setSubmitting(true)

      // Clean up facility field - set to null if bookFacility is false or facility is empty string
      if (!data.bookFacility || data.facility === '') {
        data.facility = null
      }

      // Fix timezone issue - set date to noon UTC to prevent date shifting
      if (data.eventDate) {
        const dateOnly = data.eventDate.split('T')[0] // Get YYYY-MM-DD part
        data.eventDate = `${dateOnly}T12:00:00.000Z` // Set to noon UTC
      }

      // Build FormData for multipart upload
      const formData = new FormData()
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          formData.append(key, data[key])
        }
      })

      // Attach signup roles as JSON
      if (data.hasSignupSheet && signupRoles.length > 0) {
        formData.set('signupRoles', JSON.stringify(signupRoles))
      }

      // Attach flyer file if selected
      if (flyerFile) {
        formData.append('flyer', flyerFile)
      }

      // Flag to remove existing flyer
      if (removeFlyerFlag) {
        formData.append('removeFlyer', 'true')
      }

      if (editingEvent) {
        await axios.put(`/api/events/${editingEvent._id}`, formData)
      } else {
        await axios.post('/api/events', formData)
      }
      await fetchEvents()
      closeModal()
    } catch (err) {
      alert(
        `Failed to ${editingEvent ? 'update' : 'create'} event: ${
          err.response?.data?.message || err.message
        }`
      )
    } finally {
      setSubmitting(false)
    }
  }

  // Handle delete
  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return

    try {
      await axios.delete(`/api/events/${eventId}`)
      await fetchEvents()
    } catch (err) {
      alert('Failed to delete event: ' + (err.response?.data?.message || err.message))
    }
  }

  // Fetch RSVP data for an event
  const handleViewRsvps = async (event) => {
    try {
      setLoadingRsvps(true)
      setSelectedEventForRsvp(event)
      setShowRsvpModal(true)

      const response = await axios.get(`/api/events/${event._id}/rsvps`)
      setRsvpData(response.data)
    } catch (err) {
      console.error('Error fetching RSVPs:', err)
      alert('Failed to load RSVPs: ' + (err.response?.data?.message || err.message))
      setShowRsvpModal(false)
    } finally {
      setLoadingRsvps(false)
    }
  }

  // Close RSVP modal
  const closeRsvpModal = () => {
    setShowRsvpModal(false)
    // Refresh the count for this specific event
    if (selectedEventForRsvp && rsvpData) {
      setRsvpCounts(prev => ({
        ...prev,
        [selectedEventForRsvp._id]: {
          rsvpCount: rsvpData.summary.totalRSVPs,
          totalAttendees: rsvpData.summary.totalAttendees
        }
      }))
    }
    setSelectedEventForRsvp(null)
    setRsvpData(null)
  }

  // Start editing an RSVP
  const startEditRsvp = (rsvp) => {
    setEditingRsvp(rsvp._id)
    setEditRsvpValues({ numAdults: rsvp.numAdults, numChildren: rsvp.numChildren })
  }

  // Save edited RSVP
  const saveEditRsvp = async (rsvpId) => {
    try {
      await axios.put(`/api/events/${selectedEventForRsvp._id}/rsvps/${rsvpId}`, editRsvpValues)
      // Refresh RSVP data
      const response = await axios.get(`/api/events/${selectedEventForRsvp._id}/rsvps`)
      setRsvpData(response.data)
      setEditingRsvp(null)
    } catch (err) {
      alert('Failed to update RSVP: ' + (err.response?.data?.message || err.message))
    }
  }

  // Format date in Eastern Time
  const formatDate = (dateString) => {
    return moment.tz(dateString, ET).format('MMM D, YYYY')
  }

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A'
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Get category badge
  const getCategoryBadge = (category) => {
    const badges = {
      general: 'bg-gray-100 text-gray-800',
      pool: 'bg-primary-100 text-primary-800',
      tennis: 'bg-tertiary-100 text-tertiary-800',
      social: 'bg-secondary-100 text-secondary-800',
      meeting: 'bg-primary-100 text-primary-800',
    }
    return badges[category?.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  // Filter events based on showHistorical
  const filteredEvents = showHistorical
    ? events
    : events.filter(event => {
        const eventDate = moment.tz(event.eventDate, ET)
        const today = moment().startOf('day')
        return eventDate.isSameOrAfter(today)
      })

  // Separate counts for display
  const upcomingCount = events.filter(event => {
    const eventDate = moment.tz(event.eventDate, ET)
    const today = moment().startOf('day')
    return eventDate.isSameOrAfter(today)
  }).length

  const historicalCount = events.length - upcomingCount

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Event Management</h1>
            <p className="text-gray-600 mt-1">Create and manage club events</p>
          </div>
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium flex items-center shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Event
          </button>
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Showing:</span>
              <button
                onClick={() => setShowHistorical(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !showHistorical
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                Upcoming ({upcomingCount})
              </button>
              <button
                onClick={() => setShowHistorical(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showHistorical
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                All Events ({events.length})
              </button>
            </div>
            {showHistorical && historicalCount > 0 && (
              <div className="text-sm text-gray-600">
                {historicalCount} historical event{historicalCount !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Events Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {showHistorical ? 'No events' : 'No upcoming events'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {showHistorical
              ? 'Get started by creating a new event.'
              : events.length > 0
                ? `You have ${historicalCount} historical event${historicalCount !== 1 ? 's' : ''}. Click "All Events" to view them.`
                : 'Get started by creating a new event.'
            }
          </p>
          <div className="mt-6">
            <button
              onClick={() => openModal()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              Create Event
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <div
              key={event._id}
              className={`rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow ${
                event.isPublished
                  ? 'bg-white border border-gray-100'
                  : 'bg-yellow-50/50 border-2 border-dashed border-yellow-300'
              }`}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex gap-2 flex-wrap mb-4">
                  {event.isPublished ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      Published
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Draft
                    </span>
                  )}
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${getCategoryBadge(
                      event.category
                    )}`}
                  >
                    {event.category?.charAt(0).toUpperCase() + event.category?.slice(1) || 'General'}
                  </span>
                  {moment.tz(event.eventDate, ET).isBefore(moment().startOf('day')) && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-300 text-gray-700">
                      Past Event
                    </span>
                  )}
                  {event.flyerImageUrl && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Flyer
                    </span>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                  {event.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {event.description || 'No description provided'}
                </p>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {event.eventDate ? formatDate(event.eventDate) : 'No date'}
                  </div>
                  {(event.startTime || event.endTime) && (
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {formatTime(event.startTime)} - {formatTime(event.endTime)}
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {event.location}
                    </div>
                  )}
                  {event.maxAttendees && (
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      Max {event.maxAttendees} attendees
                    </div>
                  )}
                </div>

                {/* RSVP Summary (if RSVP is required or payment is collected) */}
                {(event.requireRsvp || event.collectPayment) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className="text-sm font-medium text-gray-700 flex items-center">
                        <svg className="w-4 h-4 mr-1.5 text-tertiary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        RSVPs
                      </span>
                      <span className="text-lg font-bold text-tertiary-700">
                        {rsvpCounts[event._id] ? rsvpCounts[event._id].totalAttendees : 0}
                        <span className="text-xs font-normal text-gray-500 ml-1">attendees</span>
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleViewRsvps(event)
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 bg-tertiary-50 hover:bg-tertiary-100 text-tertiary-700 rounded-lg transition-colors"
                    >
                      <span className="flex items-center text-sm font-medium">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        View RSVPs
                      </span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Signup Sheet Summary */}
                {event.hasSignupSheet && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className="text-sm font-medium text-gray-700 flex items-center">
                        <svg className="w-4 h-4 mr-1.5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        Signups
                      </span>
                      <span className="text-lg font-bold text-orange-700">
                        {signupCounts[event._id] ? signupCounts[event._id].signupCount : 0}
                        <span className="text-xs font-normal text-gray-500 ml-1">signed up</span>
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleViewSignups(event)
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors"
                    >
                      <span className="flex items-center text-sm font-medium">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        View Signups
                      </span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => openModal(event)}
                    className="flex-1 px-3 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 font-medium text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(event._id)}
                    className="flex-1 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 font-medium text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingEvent ? 'Edit Event' : 'Create New Event'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  className={`w-full px-4 py-2 border ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  placeholder="Annual Pool Party"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="Event description..."
                />
              </div>

              {/* Event Flyer Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Flyer (JPG or PNG, max 10MB)
                </label>
                {flyerPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={flyerPreview}
                      alt="Flyer preview"
                      className="max-h-48 rounded-lg border border-gray-300 object-contain"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveFlyer}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      title="Remove flyer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={handleFlyerChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                )}
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    {...register('eventDate', { required: 'Date is required' })}
                    className={`w-full px-4 py-2 border ${
                      errors.eventDate ? 'border-red-300' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  />
                  {errors.eventDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.eventDate.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <select
                    {...register('startTime')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  >
                    <option value="">Select time</option>
                    {timeOptions.map(time => (
                      <option key={time.value} value={time.value}>
                        {time.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time
                  </label>
                  <select
                    {...register('endTime')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  >
                    <option value="">Select time</option>
                    {timeOptions.map(time => (
                      <option key={time.value} value={time.value}>
                        {time.label}
                      </option>
                    ))}
                  </select>
                  {!errors.endTime && startTime && (
                    <p className="mt-1 text-xs text-primary-600">
                      Default is 4 hours after start time
                    </p>
                  )}
                </div>
              </div>

              {/* Location and Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    {...register('location')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Pool Area"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    {...register('category')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  >
                    <option value="general">General</option>
                    <option value="pool">Pool</option>
                    <option value="tennis">Tennis</option>
                    <option value="social">Social</option>
                    <option value="meeting">Meeting</option>
                  </select>
                </div>
              </div>

              {/* RSVP and Payment Settings */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900">Event Registration Settings</h4>

                {/* Require RSVP */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="requireRsvp"
                    {...register('requireRsvp')}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="requireRsvp" className="ml-2 text-sm font-medium text-gray-700">
                    Require RSVP (to collect headcount)
                  </label>
                </div>

                {/* Collect Payment */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="collectPayment"
                    {...register('collectPayment')}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="collectPayment" className="ml-2 text-sm font-medium text-gray-700">
                    Collect payment for this event
                  </label>
                </div>

                {/* Price Fields (shown when collectPayment is true) */}
                {collectPayment && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-300">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price per Adult ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        {...register('pricePerAdult', {
                          min: { value: 0, message: 'Price must be positive' },
                          valueAsNumber: true
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="0.00"
                      />
                      {errors.pricePerAdult && (
                        <p className="mt-1 text-sm text-red-600">{errors.pricePerAdult.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price per Child ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        {...register('pricePerChild', {
                          min: { value: 0, message: 'Price must be positive' },
                          valueAsNumber: true
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="0.00"
                      />
                      {errors.pricePerChild && (
                        <p className="mt-1 text-sm text-red-600">{errors.pricePerChild.message}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Facility Booking */}
              <div className="space-y-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
                <h4 className="font-semibold text-gray-900">Facility Reservation</h4>
                <p className="text-sm text-gray-600">
                  Reserve a facility for this event. The booking will be automatically created and appear on the facility calendar.
                </p>

                {/* Book Facility Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="bookFacility"
                    {...register('bookFacility')}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="bookFacility" className="ml-2 text-sm font-medium text-gray-700">
                    Book a facility for this event
                  </label>
                </div>

                {/* Facility Selection (shown when bookFacility is true) */}
                {bookFacility && (
                  <div className="mt-4 pt-4 border-t border-primary-300">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Facility *
                    </label>
                    <select
                      {...register('facility', {
                        required: bookFacility ? 'Please select a facility' : false
                      })}
                      className={`w-full px-4 py-2 border ${
                        errors.facility ? 'border-red-300' : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white`}
                    >
                      <option value="">Choose a facility...</option>
                      <option value="pavilion">Pavilion</option>
                      <option value="taj_mahal">Taj</option>
                    </select>
                    {errors.facility && (
                      <p className="mt-1 text-sm text-red-600">{errors.facility.message}</p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Note: Make sure you have entered the event date and time above, as they will be used for the facility booking.
                    </p>
                  </div>
                )}
              </div>

              {/* Sign-Up Sheet */}
              <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-semibold text-gray-900">Sign-Up Sheet</h4>
                <p className="text-sm text-gray-600">
                  Add volunteer shifts, potluck categories, and competition entries. An event can use any
                  combination of them.
                </p>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasSignupSheet"
                    {...register('hasSignupSheet')}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="hasSignupSheet" className="ml-2 text-sm font-medium text-gray-700">
                    Enable sign-up sheet for this event
                  </label>
                </div>

                {hasSignupSheet && (
                  <div className="mt-4 pt-4 border-t border-orange-300 space-y-4">
                    {signupRoles.map((role) => {
                      const typeConfig = getSignupType(role)
                      return (
                        <div key={role.roleId} className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-700 whitespace-nowrap">
                              {typeConfig.typeLabel}
                            </span>
                            <input
                              type="text"
                              value={role.roleName}
                              onChange={(e) => updateRole(role.roleId, 'roleName', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                              placeholder={typeConfig.groupPlaceholder}
                            />
                            <button
                              type="button"
                              onClick={() => removeRole(role.roleId)}
                              className="px-2 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Remove group"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>

                          <input
                            type="text"
                            value={role.description || ''}
                            onChange={(e) => updateRole(role.roleId, 'description', e.target.value)}
                            className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                            placeholder="Optional instructions shown to members"
                          />

                          {role.slots.map((slot) => (
                            <div key={slot.slotId} className="flex items-center gap-2 mb-2 ml-4">
                              {usesTimeSlots(role) ? (
                                <>
                                  <select
                                    value={slot.startTime}
                                    onChange={(e) => updateSlot(role.roleId, slot.slotId, 'startTime', e.target.value)}
                                    className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                  >
                                    <option value="">Start</option>
                                    {timeOptions.map(t => (
                                      <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                  </select>
                                  <span className="text-gray-400 text-sm">to</span>
                                  <select
                                    value={slot.endTime}
                                    onChange={(e) => updateSlot(role.roleId, slot.slotId, 'endTime', e.target.value)}
                                    className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                  >
                                    <option value="">End</option>
                                    {timeOptions.map(t => (
                                      <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                  </select>
                                </>
                              ) : (
                                <input
                                  type="text"
                                  value={slot.label || ''}
                                  onChange={(e) => updateSlot(role.roleId, slot.slotId, 'label', e.target.value)}
                                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                  placeholder={typeConfig.slotPlaceholder}
                                />
                              )}
                              <input
                                type="number"
                                min="1"
                                value={slot.capacity}
                                onChange={(e) => updateSlot(role.roleId, slot.slotId, 'capacity', e.target.value)}
                                className="w-16 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                title={typeConfig.capacityTitle}
                              />
                              <span className="text-xs text-gray-500">max</span>
                              {role.slots.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeSlot(role.roleId, slot.slotId)}
                                  className="text-red-400 hover:text-red-600"
                                  title="Remove slot"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={() => addSlot(role.roleId)}
                            className="ml-4 mt-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            {typeConfig.addSlotLabel}
                          </button>
                        </div>
                      )
                    })}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {Object.entries(SIGNUP_TYPES).map(([type, config]) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => addRole(type)}
                          title={config.typeHint}
                          className="py-2 px-2 border-2 border-dashed border-orange-300 rounded-lg text-sm font-medium text-orange-600 hover:bg-orange-100 transition-colors"
                        >
                          + {config.typeLabel}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Max Attendees and Published */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Attendees
                  </label>
                  <input
                    type="number"
                    {...register('maxAttendees', { min: 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-3 pt-8">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isPublished"
                      {...register('isPublished')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="isPublished" className="ml-2 text-sm font-medium text-gray-700">
                      Publish event (make visible to members)
                    </label>
                  </div>
                  {!editingEvent && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="notifyMembers"
                        {...register('notifyMembers')}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="notifyMembers" className="ml-2 text-sm font-medium text-gray-700">
                        Email all members about this event
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RSVP Details Modal */}
      {showRsvpModal && selectedEventForRsvp && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Event RSVPs</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedEventForRsvp.title}</p>
              </div>
              <button onClick={closeRsvpModal} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6">
              {loadingRsvps ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : rsvpData ? (
                <>
                  {/* Summary Cards */}
                  <div className={`grid grid-cols-1 ${selectedEventForRsvp.collectPayment ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 mb-6`}>
                    <div className="bg-primary-50 rounded-lg p-4">
                      <div className="text-sm text-primary-600 font-medium">Total RSVPs</div>
                      <div className="text-2xl font-bold text-primary-900 mt-1">
                        {rsvpData.summary.totalRSVPs}
                      </div>
                    </div>
                    <div className="bg-tertiary-50 rounded-lg p-4">
                      <div className="text-sm text-tertiary-600 font-medium">Total Attendees</div>
                      <div className="text-2xl font-bold text-green-900 mt-1">
                        {rsvpData.summary.totalAttendees}
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-purple-600 font-medium">Adults / Children</div>
                      <div className="text-2xl font-bold text-purple-900 mt-1">
                        {rsvpData.summary.totalAdults} / {rsvpData.summary.totalChildren}
                      </div>
                    </div>
                    {selectedEventForRsvp.collectPayment && (
                      <div className="bg-orange-50 rounded-lg p-4">
                        <div className="text-sm text-orange-600 font-medium">Total Revenue</div>
                        <div className="text-2xl font-bold text-orange-900 mt-1">
                          ${rsvpData.summary.totalRevenue.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RSVP List */}
                  {rsvpData.rsvps.length > 0 ? (
                    <div className="border border-gray-200 rounded-lg overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Adults
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Children
                            </th>
                            {selectedEventForRsvp.collectPayment && (
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                            )}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Confirmation #
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {rsvpData.rsvps.map((rsvp) => (
                            <tr key={rsvp._id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {rsvp.attendeeName}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {rsvp.attendeeEmail}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {editingRsvp === rsvp._id ? (
                                  <input
                                    type="number"
                                    min="0"
                                    value={editRsvpValues.numAdults}
                                    onChange={(e) => setEditRsvpValues(prev => ({ ...prev, numAdults: parseInt(e.target.value) || 0 }))}
                                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                  />
                                ) : rsvp.numAdults}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {editingRsvp === rsvp._id ? (
                                  <input
                                    type="number"
                                    min="0"
                                    value={editRsvpValues.numChildren}
                                    onChange={(e) => setEditRsvpValues(prev => ({ ...prev, numChildren: parseInt(e.target.value) || 0 }))}
                                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                                  />
                                ) : rsvp.numChildren}
                              </td>
                              {selectedEventForRsvp.collectPayment && (
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  ${(rsvp.totalAmount || 0).toFixed(2)}
                                </td>
                              )}
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 font-mono">
                                {rsvp.confirmationNumber}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {moment.tz(rsvp.createdAt, ET).format('MMM D, YYYY')}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">
                                {editingRsvp === rsvp._id ? (
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => saveEditRsvp(rsvp._id)}
                                      className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingRsvp(null)}
                                      className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => startEditRsvp(rsvp)}
                                    className="px-2 py-1 bg-primary-50 text-primary-700 rounded text-xs hover:bg-primary-100 font-medium"
                                  >
                                    Edit
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No RSVPs yet</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        No one has registered for this event yet.
                      </p>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <button
                onClick={closeRsvpModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signup Details Modal */}
      {showSignupModal && selectedEventForSignup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Event Signups</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedEventForSignup.title}</p>
              </div>
              <button onClick={closeSignupModal} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-6">
              {loadingSignups ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : signupData ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {(signupData.summary.breakdown || []).map(b => (
                      <div key={b.type} className="bg-orange-50 rounded-lg p-4">
                        <div className="text-sm text-orange-600 font-medium">
                          {SIGNUP_TYPES[b.type]?.summaryLabel || b.type}
                        </div>
                        <div className="text-2xl font-bold text-orange-900 mt-1">
                          {b.filled} <span className="text-base font-medium text-orange-500">/ {b.capacity}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Signups grouped by role/slot */}
                  {selectedEventForSignup.signupRoles?.map(role => (
                    <div key={role.roleId} className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">{role.roleName}</h4>
                        <span className="px-2 py-1 rounded text-xs font-semibold bg-orange-100 text-orange-700">
                          {getSignupType(role).typeLabel}
                        </span>
                      </div>
                      {role.slots.map(slot => {
                        const slotSignups = signupData.signups.filter(
                          s => s.roleId === role.roleId && s.slotId === slot.slotId && s.status === 'active'
                        )
                        return (
                          <div key={slot.slotId} className="mb-4 ml-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                {formatSlotLabel(role, slot, formatTime)}
                              </span>
                              <span className={`text-sm font-medium ${slotSignups.length >= slot.capacity ? 'text-red-600' : 'text-green-600'}`}>
                                {slotSignups.length} / {slot.capacity} filled
                              </span>
                            </div>
                            {slotSignups.length > 0 ? (
                              <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                      {requiresDetail(role) && (
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          {getSignupType(role).detailLabel}
                                        </th>
                                      )}
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {slotSignups.map(signup => (
                                      <tr key={signup._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                          {editingSignup === signup._id ? (
                                            <input
                                              type="text"
                                              value={editSignupValues.attendeeName}
                                              onChange={(e) => setEditSignupValues(prev => ({ ...prev, attendeeName: e.target.value }))}
                                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                          ) : signup.attendeeName}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                          {editingSignup === signup._id ? (
                                            <input
                                              type="email"
                                              value={editSignupValues.attendeeEmail}
                                              onChange={(e) => setEditSignupValues(prev => ({ ...prev, attendeeEmail: e.target.value }))}
                                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                          ) : signup.attendeeEmail}
                                        </td>
                                        {requiresDetail(role) && (
                                          <td className="px-4 py-2 text-sm text-gray-600">
                                            {editingSignup === signup._id ? (
                                              <input
                                                type="text"
                                                value={editSignupValues.detail}
                                                onChange={(e) => setEditSignupValues(prev => ({ ...prev, detail: e.target.value }))}
                                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                                              />
                                            ) : signup.detail}
                                          </td>
                                        )}
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                          {moment.tz(signup.createdAt, ET).format('MMM D, YYYY')}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                          {editingSignup === signup._id ? (
                                            <div className="flex gap-1">
                                              <button
                                                onClick={() => saveEditSignup(signup._id)}
                                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                              >
                                                Save
                                              </button>
                                              <button
                                                onClick={() => setEditingSignup(null)}
                                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          ) : (
                                            <div className="flex gap-1">
                                              <button
                                                onClick={() => startEditSignup(signup)}
                                                className="px-2 py-1 bg-primary-50 text-primary-700 rounded text-xs hover:bg-primary-100 font-medium"
                                              >
                                                Edit
                                              </button>
                                              <button
                                                onClick={() => deleteSignup(signup._id)}
                                                className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs hover:bg-red-100 font-medium"
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 italic ml-2">No signups yet</p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ))}

                  {signupData.signups.filter(s => s.status === 'active').length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No signups yet</h3>
                      <p className="mt-1 text-sm text-gray-500">No one has signed up for shifts yet.</p>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <button
                onClick={closeSignupModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventManagement
